import { orgAPI } from "@/api/api";
import { useCreateFlowSession } from "@/api/flow_sessions";
import { usePostFlowMessage } from "@/api/messages";
import { UploadTarget } from "@/hooks/useFileRecordUpload";
import { useFileRecordUploadQueue } from "@/hooks/useFileRecordUploadQueue";
import { wait } from "@/utils/wait";
import { FLOW_SESSION_MODES, FileRecord } from "@cohesive/api-client";
import { format } from "date-fns";
import { useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

/**
 * The two voice-transcript destinations: send as a chat message (workspace or
 * canvas scoped) or save as a markdown file record (workspace or canvas).
 *
 * Chat targets are chosen at runtime from the destination sheet, so this
 * builds the flow session imperatively rather than through useMessageActions
 * (which fixes its workspace at hook-call time). Canvas chat follows the
 * useEnsureCanvasAgentChatOpen pattern — reuse the most recent main session,
 * else mint a CANVAS_CHAT session — but fetches the session list, since the
 * mobile cache is cold.
 */
export function useVoiceDestinations() {
  const createFlowSession = useCreateFlowSession();
  const postFlowMessage = usePostFlowMessage();
  const queue = useFileRecordUploadQueue();

  const postMessage = useCallback(
    async (flowSessionId: string, content: string) => {
      await postFlowMessage.mutateAsync({
        flowSessionId,
        id: uuidv4(),
        content,
        file_record_ids: [],
        referenced_entities: [],
      });
    },
    [postFlowMessage],
  );

  const sendToWorkspace = useCallback(
    async (
      workspaceId: string,
      content: string,
    ): Promise<{ flowSessionId: string }> => {
      const flowSession = await createFlowSession.mutateAsync({
        mode: FLOW_SESSION_MODES.WORKSPACE_CHAT,
        class: "workspace",
        workspace_id: workspaceId,
      });
      // Same cushion useMessageActions gives a fresh session before its
      // first message.
      await wait(150);
      await postMessage(flowSession.id, content);
      return { flowSessionId: flowSession.id };
    },
    [createFlowSession, postMessage],
  );

  const sendToCanvas = useCallback(
    async (
      canvasId: string,
      content: string,
    ): Promise<{ flowSessionId: string }> => {
      // Prefer the canvas's most recent main chat (action-item sessions are
      // node-scoped, not the canvas-wide agent chat).
      const sessions = await orgAPI.canvas
        .getFlowSessions(canvasId)
        .catch(() => []);
      const mainSessions = sessions.filter((s) => !s.action_item_id);
      const mostRecent = [...mainSessions].sort(
        (a, b) =>
          new Date(b.updated_at ?? 0).getTime() -
          new Date(a.updated_at ?? 0).getTime(),
      )[0];

      let flowSessionId: string;
      if (mostRecent) {
        flowSessionId = mostRecent.id;
      } else {
        const created = await createFlowSession.mutateAsync({
          mode: FLOW_SESSION_MODES.CANVAS_CHAT,
          class: "canvas",
          canvas_id: canvasId,
        });
        flowSessionId = created.id;
        await wait(150);
      }
      await postMessage(flowSessionId, content);
      return { flowSessionId };
    },
    [createFlowSession, postMessage],
  );

  /**
   * Create a real-bytes `.md` file record at the target. Resolves once the
   * S3 POST lands (content durably stored) — that's the delivery signal the
   * voice draft can clear on.
   */
  const saveMarkdown = useCallback(
    (target: UploadTarget, content: string): Promise<FileRecord> => {
      const name = `Voice note — ${format(new Date(), "MMM d, h.mm a")}.md`;
      const file = new File([content], name, { type: "text/markdown" });
      return new Promise<FileRecord>((resolve, reject) => {
        let settled = false;
        void queue
          .enqueue(
            [{ file, contentType: "text/markdown", kind: "create" }],
            target,
            {
              onUploaded: (record) => {
                if (settled) return;
                settled = true;
                resolve(record);
              },
              onUploadError: (err) => {
                if (settled) return;
                settled = true;
                reject(err instanceof Error ? err : new Error("Upload failed"));
              },
            },
          )
          .then(() => {
            // enqueue resolves after all uploads settle; if neither callback
            // fired (e.g. file rejected by the size gate), fail explicitly.
            if (!settled) {
              settled = true;
              reject(new Error("Upload did not start"));
            }
          })
          .catch((err) => {
            // enqueue itself can reject before any per-file callback runs
            // (e.g. the existing-names lookup fails). Without this the promise
            // never settles and the review screen stays stuck "delivering".
            if (settled) return;
            settled = true;
            reject(err instanceof Error ? err : new Error("Upload failed"));
          });
      });
    },
    [queue],
  );

  return { sendToWorkspace, sendToCanvas, saveMarkdown };
}
